import React from 'react';
import { InsuranceDataType } from '../Settings/types';
import { Box, Typography } from '@mui/material';
import { ValidationStatus } from '@uplift/api';
import { Autorenew, CheckCircle, Error, LabelImportant } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { useUserDisplayNameQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { EditableSectionRow } from '../../..//EditableSection';

export type AdminInsuranceStatusRowProps = {
  insuranceData: InsuranceDataType;
};

export const AdminInsuranceStatusRow: React.FC<AdminInsuranceStatusRowProps> = ({ insuranceData }) => {
  const hidden = insuranceData?.isSecondaryInsurance;

  return (
    <EditableSectionRow
      label="Status"
      rowKey="status"
      kind="custom"
      hideRowDisplay={hidden}
    >
      <AdminInsuranceStatus insuranceData={insuranceData} />
    </EditableSectionRow>
  );
};

type AdminInsuranceStatusProps = {
  insuranceData: InsuranceDataType;
};

const AdminInsuranceStatus: React.FC<AdminInsuranceStatusProps> = props => {
  const { insuranceData } = props;
  const { classes } = useStyle();
  const { data: verifiedByUser } = useUserDisplayNameQuery(insuranceData.verifiedBy ?? '', {
    enabled: !!insuranceData.verifiedBy && insuranceData.status === ValidationStatus.manuallyVerified,
    suspense: true,
  });
  const verifiedAt = CalendarDateService.date(insuranceData.updatedAt).tz(CalendarDateService.timezones.ET).format('M/D/YYYY, h:mm A ET');

  switch (insuranceData.status) {
    case ValidationStatus.pending:
      return (
        <StatusDisplay
          icon={<Autorenew className={classes.pending} fontSize="small" />}
          label="Pending Verification"
          description="This insurance is pending verification."
        />
      );
    case ValidationStatus.needsReview:
      return (
        <StatusDisplay
          icon={<Error color="error" />}
          label="Needs Review"
          description="This insurance needs to be reviewed."
        />
      );
    case ValidationStatus.manuallyVerified:
      return (
        <StatusDisplay
          icon={<LabelImportant color="warning" />}
          label="Manually Verified"
          description={`by ${verifiedByUser?.defaultDisplay} ${verifiedAt}`}
        />
      );
    case ValidationStatus.active:
      return (
        <StatusDisplay
          icon={<CheckCircle color="success" />}
          label="Verified"
          description={`Verified by UpLift Bot ${verifiedAt}`}
        />
      );
    default:
      return null;
  }
};

const useStyle = makeStyles()(theme => ({
  pending: {
    color: theme.palette.text.hint,
  }
}));

type StatusDisplayProps = {
  icon: React.ReactNode;
  label: string;
  description: string;
};

const StatusDisplay: React.FC<StatusDisplayProps> = props => {
  return (
    <div>
      <Box display="flex" gap="0.5rem" alignItems="center">
        {props.icon}
        <Typography fontWeight={500}>{props.label}</Typography>
      </Box>
      <Typography variant="body2" mt={1}>{props.description}</Typography>
    </div>
  );
};